import { api, ifMatch } from "@/lib/api";
import type { Cursor, Page } from "@/lib/types";

/** The PSGC hierarchy: province → city → barangay. */

interface Audited {
  id: number;
  created_by_id: number | null;
  updated_by_id: number | null;
  created_at: string;
  updated_at: string;
}

interface Located {
  population: number | null;
  latitude: number | null;
  longitude: number | null;
}

export interface Province extends Audited, Located {
  name: string;
}

export interface City extends Audited, Located {
  name: string;
  code: string;
  province_id: number;
  province_name: string | null;
}

export interface Barangay extends Audited, Located {
  name: string;
  code: string;
  city_id: number;
  city_name: string | null;
  province_id: number;
  province_name: string | null;
}

/** The API refuses anything larger; every "all of them" fetch uses this. */
export const PAGE_SIZE_CAP = 100;

export type ListParams = Record<
  string,
  string | string[] | number | boolean | null | undefined
>;

/** Drop empty values so the request carries only what was chosen. */
function clean(params: ListParams): Record<string, string | string[] | number | boolean> {
  return Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string | string[] | number | boolean] =>
        entry[1] !== undefined && entry[1] !== null && entry[1] !== "",
    ),
  );
}

/** A query string has no lists: one is sent comma-joined, the way those endpoints read it. */
function flat(params: ListParams): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(clean(params)).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(",") : value,
    ]),
  );
}

export async function listResource<T>(
  endpoint: string,
  params: ListParams,
  signal?: AbortSignal,
): Promise<Page<T>> {
  return (
    await api.get<Page<T>>(endpoint, { params: flat(params), ...(signal ? { signal } : {}) })
  ).data;
}

/**
 * The same page, asked for with the filters in a JSON body: `POST {endpoint}/query`.
 * A body has no length limit, so a saved filter or a fifty-club selection goes as it is.
 */
export async function queryResource<T>(
  endpoint: string,
  params: ListParams,
  signal?: AbortSignal,
): Promise<Page<T>> {
  return (await api.post<Page<T>>(`${endpoint}/query`, clean(params), signal ? { signal } : {}))
    .data;
}

/** A screenful of a cursor list; pass the answer's `next` back as `after` for the rest. */
export async function listCursor<T>(
  endpoint: string,
  params: ListParams,
  signal?: AbortSignal,
): Promise<Cursor<T>> {
  return (
    await api.get<Cursor<T>>(endpoint, { params: flat(params), ...(signal ? { signal } : {}) })
  ).data;
}

/** Every row, page after page — for a catalogue that must be complete, like permissions. */
export async function listAll<T>(
  endpoint: string,
  params: ListParams = {},
  signal?: AbortSignal,
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page += 1) {
    // Checked between pages as well as inside them: abandoning this halfway
    // should stop the next request, not only the one already out.
    signal?.throwIfAborted();
    const result = await listResource<T>(
      endpoint,
      { ...params, page, size: PAGE_SIZE_CAP },
      signal,
    );
    items.push(...result.items);
    if (page >= result.pages || result.items.length === 0) return items;
  }
}

export async function createResource<T>(endpoint: string, body: object): Promise<T> {
  return (await api.post<T>(endpoint, body)).data;
}

export async function updateResource<T>(
  endpoint: string,
  id: number,
  body: object,
  updatedAt?: string | null,
  /** A sub-route under the record — `basic-info` — when one card of it is edited at a time. */
  action?: string,
): Promise<T> {
  const url = action ? `${endpoint}/${id}/${action}` : `${endpoint}/${id}`;
  return (await api.patch<T>(url, body, ifMatch({ updated_at: updatedAt }))).data;
}

export async function deleteResource(
  endpoint: string,
  id: number,
  updatedAt?: string | null,
): Promise<void> {
  await api.delete(`${endpoint}/${id}`, ifMatch({ updated_at: updatedAt }));
}

/** A `/compact` list as select options. Pages at 10 by default; ask for `limit` to label a table. */
export async function compactOptions(
  endpoint: string,
  params: ListParams = {},
  signal?: AbortSignal,
): Promise<{ value: number; label: string }[]> {
  const { data } = await api.get<{ id: number; name: string }[]>(endpoint, {
    params: clean(params),
    ...(signal ? { signal } : {}),
  });
  return data.map((row) => ({ value: row.id, label: row.name }));
}

/** Every live account, for labelling "created by" cells. Dropdowns use FkSelect instead. */
export const userOptions = (signal?: AbortSignal) =>
  compactOptions("/users/compact", { limit: 200 }, signal);
/** Every club, for labelling club cells. */
export const clubOptions = (signal?: AbortSignal) =>
  compactOptions("/clubs/compact", { limit: 200 }, signal);
